import React from 'react'
import {
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Stack,
  Paper,
  InputAdornment,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import PersonIcon from '@mui/icons-material/Person'

import { removeDuplicates } from 'utils/remove-duplicates'

import intl from 'localization/components'
import { ConditionallyRender, ImportDropzone } from 'components'

interface User {
  userName: string
  email: string
}

interface SelectFieldsProps {
  selectedFields: string[]
  setSelectedFields: (fields: string[]) => void
  rows: Record<string, any>[]
}

const SelectFields = ({ selectedFields, setSelectedFields, rows }: SelectFieldsProps) => {
  return (
    <ConditionallyRender
      condition={selectedFields.length === 0}
      when={
        <Box>
          <Typography variant="subtitle2" gutterBottom>Select User Name field</Typography>
          <Box>
            {Object.keys(rows[0] ?? {}).map((x) => (
              <List key={x} dense>
                <ListItem
                  component="button"
                  onClick={() => setSelectedFields([...selectedFields, x])}
                  sx={{ borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <ListItemText primary={x} secondary={rows[0][x]} />
                </ListItem>
              </List>
            ))}
          </Box>
        </Box>
      }
      otherwise={
        <Box>
          <Typography variant="subtitle2" gutterBottom>Select Email field</Typography>
          <Box>
            {Object.keys(rows[0] ?? {}).map((x) =>
              !selectedFields.includes(x) ? (
                <List key={x} dense>
                  <ListItem
                    component="button"
                    onClick={() => setSelectedFields([...selectedFields, x])}
                    sx={{ borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <ListItemText primary={x} secondary={rows[0][x]} />
                  </ListItem>
                </List>
              ) : null,
            )}
          </Box>
        </Box>
      }
    />
  )
}

interface CreateUsersProps {
  handleCreateCustomerChange?: any
  createCustomerState?: any
  messages: Record<string, unknown>
  responsibleOptions?: any[]
  users: User[]
  setUsers: (users: User[]) => void
  validateUsers?: boolean
  setValidateUsers: (valid: boolean) => void
}

const CreateUsers = ({ messages, users, setUsers, setValidateUsers }: CreateUsersProps) => {
  const [hasSelectedUsers, setHasSelectedUsers] = React.useState(false)
  const [rows, setRows] = React.useState<Record<string, any>[]>([])
  const [selectedFields, setSelectedFields] = React.useState<string[]>([])

  const updateTextField = (index: number, key: string, value: string) => {
    setUsers(
      users.map((x, i) => {
        if (i !== index) {
          return x
        } else {
          return {
            ...x,
            [key]: value,
          }
        }
      }),
    )
  }

  const handleSetFieldList = React.useCallback((rows: Record<string, any>[] | null) => {
    setRows(rows ?? [])
    setSelectedFields([])
    setHasSelectedUsers(false)
  }, [])

  const restoreDefaults = () => {
    setRows([])
    setSelectedFields([])
  }

  React.useEffect(() => {
    if (selectedFields.length === 2) {
      const name = selectedFields[0]
      const email = selectedFields[1]
      setUsers(
        [
          ...users,
          ...rows.map((x) => ({
            userName: x[name],
            email: x[email],
          })),
        ].filter((x) => x.userName !== ''),
      )
      setHasSelectedUsers(true)
      restoreDefaults()
    }
  }, [rows, selectedFields, setUsers, users])

  function validateEmail(email: string) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
  }

  const validate = React.useCallback(
    (index: number, ret?: string) => {
      const user = users?.[index]
      const value = user?.email ?? ''
      if (value.length) {
        if (validateEmail(value)) {
          const sameEmails = users.filter(({ email }) => email === value)
          if (sameEmails.length < 2) {
            return ret === 'bool' ? true : ''
          } else {
            return ret === 'bool'
              ? false
              : 'Duplicate emails! Click the X to delete duplicates!'
          }
        } else {
          return ret === 'bool' ? false : 'Invalid email format!'
        }
      } else {
        return ret === 'bool' ? false : 'Empty field'
      }
    },
    [users],
  )

  React.useEffect(() => {
    if (!users || users.length === 0) {
      setValidateUsers(false)
      return
    }
    const valid = users.filter((x, i) => validate(i))
    if (valid?.length === 0) {
      setValidateUsers(true)
    } else {
      setValidateUsers(false)
    }
  }, [setValidateUsers, users, validate])

  const handleIconClick = (i: number) => {
    if (
      validate(i, 'text') ===
      'Duplicate emails! Click the X to delete duplicates!'
    ) {
      setUsers(removeDuplicates<User>(users, 'email'))
    }
  }

  return (
    <Stack spacing={2}>
      <ImportDropzone onFileLoaded={handleSetFieldList} />

      <ConditionallyRender
        condition={!hasSelectedUsers && rows.length > 0 && selectedFields.length < 2}
        when={
          <SelectFields
            selectedFields={selectedFields}
            setSelectedFields={setSelectedFields}
            rows={rows}
          />
        }
      />

      {(users ?? []).map((user, i) => {
        const hasEmailInput = (user?.email ?? '').length > 0
        const isValid = validate(i, 'bool')
        const errorText = validate(i, 'text')
        const isDuplicate = errorText === 'Duplicate emails! Click the X to delete duplicates!'

        return (
          <Paper
            key={`user-${i}`}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1.5,
              position: 'relative',
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    User {i + 1}
                  </Typography>
                </Stack>
                {i > 0 && (
                  <IconButton
                    size="small"
                    onClick={() => setUsers(users.filter((_, index) => index !== i))}
                    sx={{
                      color: 'error.main',
                      '&:hover': { bgcolor: 'error.lighter' },
                    }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>

              <TextField
                value={user.userName}
                label={messages[intl.generic('first-name')]}
                onChange={(e) => updateTextField(i, 'userName', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? '#2D2D2D' : 'background.paper',
                  },
                }}
              />

              <TextField
                value={user.email}
                type="email"
                label={messages[intl.generic('emails')]}
                onChange={(e) => updateTextField(i, 'email', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                error={hasEmailInput && !isValid}
                helperText={hasEmailInput && !isValid ? errorText : ' '}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? '#2D2D2D' : 'background.paper',
                  },
                }}
                InputProps={{
                  endAdornment: hasEmailInput ? (
                    <InputAdornment position="end">
                      {isValid ? (
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <ErrorIcon
                          sx={{
                            color: 'error.main',
                            fontSize: 20,
                            cursor: isDuplicate ? 'pointer' : 'default',
                          }}
                          onClick={() => isDuplicate && handleIconClick(i)}
                        />
                      )}
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Stack>
          </Paper>
        )
      })}

      <Box
        onClick={() => setUsers([...users, { userName: '', email: '' }])}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 1.5,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1.5,
          cursor: 'pointer',
          color: 'text.secondary',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(84, 189, 203, 0.08)'
                : 'primary.lighter',
          },
        }}
      >
        <AddIcon fontSize="small" />
        <Typography variant="body2">Add another user</Typography>
      </Box>
    </Stack>
  )
}

export default CreateUsers
